/* eslint-disable max-len */
import {InfoCircleOutlined} from '@ant-design/icons';
import {Checkbox, Form, Input, Radio, Select, Space} from 'antd';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import {NamePath, StoreValue} from 'antd/es/form/interface';
import moment from 'moment';
import React, {useState} from 'react';

import MomentDatePicker from '../../../components/DatePicker/MomentDatePicker';
import {filterSelectOptions} from '../../../components/Forms/Utils/commonFunctions';
import NumberInput from '../../../shared/NumberInput';
import useJSONStore from '../../../store/StaticJson/jsonStore';
import {inputType} from '../interface/inputType';

const {TextArea} = Input;

type FieldStates = Record<string, string | undefined>;

const QuestionnaireAddMoreFieldsSwitch: React.FC<{
  field: any;
  data?: any;
  getFieldValue: (name: NamePath) => StoreValue;
}> = ({field, getFieldValue}) => {
  const countryOptions = useJSONStore((state) => state.country);
  const stateOptions = useJSONStore((state) => state.states);
  const cityOptions = useJSONStore((state) => state.city);

  const [showTextBox, setShowTextBox] = useState<FieldStates>({});
  const [radioGroupIfNoThenShowTextBox, setRadioGroupIfNoThenShowTextBox] =
    useState<FieldStates>({});

  const FormInstance = useFormInstance();
  const {setFieldValue} = FormInstance;

  const {
    HEADING,
    HEADING_2,
    TEXT,
    NUMBER,
    SELECT,
    CITY,
    STATE,
    COUNTRY,
    RADIOGROUP,
    RADIOGROUP_IF_NO_THEN_TEXTBOX,
    TEXTAREA,
    PERCENTAGE,
    CHECKBOX,
    CHECKBOXGROUP,
    DATEPICKER
  } = inputType;

  const handleOnChange = (fieldName: any, value: any) => {
    const shouldShowTextBox =
      value === 'Other' ||
      value === 'Other, please specify' ||
      (typeof value === 'string' &&
        value?.startsWith('Other ') &&
        value?.endsWith('please specify'));
    setShowTextBox({
      ...showTextBox,
      [fieldName]: shouldShowTextBox
    });
  };

  const handleOnChange1 = (fieldName: any, value: any) => {
    setRadioGroupIfNoThenShowTextBox({
      ...radioGroupIfNoThenShowTextBox,
      [fieldName]: value === 'No'
    });
  };

  const tooltipCSS = {
    width: 'auto',
    maxWidth: '500px',
    height: 'auto',
    padding: '12px'
  };

  switch (field.fieldType) {
    case HEADING:
      return <h5>{field.heading}</h5>;
    case HEADING_2:
      return <h5>{field.heading}</h5>;
    case TEXT:
      return (
        <Form.Item
          name={field.fieldName}
          label={field.fieldLabel}
          tooltip={
            field.tooltip
              ? {
                  title: field.tooltip,
                  icon: <InfoCircleOutlined />,
                  overlayStyle: tooltipCSS
                }
              : ''
          }
          className={field?.colSpan ? '' : 'col-span-center'}
          labelCol={field?.colSpan ? {span: 24} : undefined}
        >
          <Input placeholder={field.placeholder} />
        </Form.Item>
      );
    case TEXTAREA:
      return (
        <Form.Item
          name={field.fieldName}
          label={field.fieldLabel}
          tooltip={
            field.tooltip
              ? {
                  title: field.tooltip,
                  icon: <InfoCircleOutlined />,
                  overlayStyle: tooltipCSS
                }
              : ''
          }
          className={field?.colSpan ? '' : 'col-span-center'}
          labelCol={field?.colSpan ? {span: 24} : undefined}
        >
          <TextArea
            style={{
              verticalAlign: 'top'
            }}
            autoSize={{minRows: 4, maxRows: 10}}
            placeholder={field.placeholder}
          />
        </Form.Item>
      );
    case NUMBER:
      return (
        <Form.Item
          name={
            field.units
              ? [field.index, field.fieldName[1], 'value']
              : field.fieldName
          }
          label={field.fieldLabel}
          className={field?.colSpan ? '' : 'col-span-center'}
          tooltip={
            field.tooltip
              ? {
                  title: field.tooltip,
                  icon: <InfoCircleOutlined />,
                  overlayStyle: tooltipCSS
                }
              : ''
          }
          // style={field.colSpan ? {alignItems: 'end'} : {}}
          labelCol={field?.colSpan ? {span: 24} : undefined}
        >
          <NumberInput
            min={field.min}
            max={field.max}
            defaultValue={field.defaultValue}
            placeholder={field.placeholder ?? 'Number Field'}
            style={{verticalAlign: 'top'}}
            roundTo={field?.roundTo}
            addonAfter={
              !field.units ? null : typeof field.units === 'string' ? (
                <Form.Item
                  name={
                    field.units && [field.index, field.fieldName[1], 'unit']
                  }
                  initialValue={field.units}
                  noStyle
                >
                  {field.units}
                </Form.Item>
              ) : (
                <Form.Item
                  name={
                    field.units && [field.index, field.fieldName[1], 'unit']
                  }
                  noStyle
                >
                  <Select
                    style={{width: 140}}
                    placeholder="Select Unit"
                    options={field.units}
                  />
                </Form.Item>
              )
            }
          />
        </Form.Item>
      );
    case SELECT:
      return (
        <>
          <Form.Item
            name={field.fieldName}
            label={field.fieldLabel}
            tooltip={
              field.tooltip
                ? {
                    title: field.tooltip,
                    icon: <InfoCircleOutlined />,
                    overlayStyle: tooltipCSS
                  }
                : ''
            }
            className={field?.colSpan ? '' : 'col-span-center'}
            labelCol={field?.colSpan ? {span: 24} : undefined}
          >
            <Select
              defaultValue={field.defaultValue}
              style={{width: 120}}
              options={field.options}
              onChange={(value) => {
                handleOnChange(field.fieldName, value);
              }}
            />
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {({getFieldValue}) => {
              const fieldValue = getFieldValue(field.fieldName);
              const shouldShow =
                fieldValue === 'Other' ||
                fieldValue === 'Other, please specify' ||
                (typeof fieldValue === 'string' &&
                  fieldValue?.startsWith('Other ') &&
                  fieldValue?.endsWith('please specify')) ||
                showTextBox[field.fieldName];

              return shouldShow ? (
                <Form.Item
                  name={field.fieldName + '_other'}
                  label="Please specify"
                  rules={[{required: true, message: 'Please specify'}]}
                >
                  <Input placeholder="Please specify" />
                </Form.Item>
              ) : null;
            }}
          </Form.Item>
        </>
      );
    case CITY:
      return (
        <Form.Item
          name={field.fieldName}
          label={field.fieldLabel}
          tooltip={
            field.tooltip
              ? {
                  title: field.tooltip,
                  icon: <InfoCircleOutlined />,
                  overlayStyle: tooltipCSS
                }
              : ''
          }
          shouldUpdate
          className={field?.colSpan ? '' : 'col-span-center'}
          labelCol={field?.colSpan ? {span: 24} : undefined}
        >
          <Select
            showSearch
            defaultValue={field.defaultValue}
            style={{width: 150}}
            options={cityOptions.filter(
              (item) =>
                item?.state_id ===
                getFieldValue([field.mainKey, field.index, field.relatedKey])
            )}
            filterOption={filterSelectOptions}
            placeholder="Select City"
          />
        </Form.Item>
      );
    case STATE:
      return (
        <Form.Item
          name={field.fieldName}
          label={field.fieldLabel}
          tooltip={
            field.tooltip
              ? {
                  title: field.tooltip,
                  icon: <InfoCircleOutlined />,
                  overlayStyle: tooltipCSS
                }
              : ''
          }
          className={field?.colSpan ? '' : 'col-span-center'}
          labelCol={field?.colSpan ? {span: 24} : undefined}
        >
          <Select
            showSearch
            defaultValue={field.defaultValue}
            style={{width: 150}}
            options={stateOptions.filter((item) => item?.country_id === 101)}
            filterOption={filterSelectOptions}
            placeholder="Select State"
            onChange={() => {
              setFieldValue(
                [field.mainKey, field.index, field.relatedKey],
                undefined
              );
            }}
          />
        </Form.Item>
      );
    case COUNTRY:
      return (
        <Form.Item
          name={field.fieldName}
          label={field.fieldLabel}
          tooltip={
            field.tooltip
              ? {
                  title: field.tooltip,
                  icon: <InfoCircleOutlined />,
                  overlayStyle: tooltipCSS
                }
              : ''
          }
          className={field?.colSpan ? '' : 'col-span-center'}
          labelCol={field?.colSpan ? {span: 24} : undefined}
        >
          <Select
            showSearch
            defaultValue={field.defaultValue}
            style={{width: 150}}
            options={countryOptions}
            filterOption={filterSelectOptions}
            placeholder="Select Country"
          />
        </Form.Item>
      );
    case PERCENTAGE:
      return (
        <Form.Item
          name={field.fieldName}
          label={field.fieldLabel}
          tooltip={
            field.tooltip
              ? {
                  title: field.tooltip,
                  icon: <InfoCircleOutlined />,
                  overlayStyle: tooltipCSS
                }
              : ''
          }
          className={field?.colSpan ? '' : 'col-span-center'}
          labelCol={field?.colSpan ? {span: 24} : undefined}
        >
          <NumberInput
            min={1}
            max={100}
            name={field.fieldName}
            placeholder={field.placeholder}
            style={{width: 300, verticalAlign: 'top'}}
          />
        </Form.Item>
      );
    case DATEPICKER:
      return (
        <Form.Item
          name={field.fieldName}
          label={field.fieldLabel}
          tooltip={
            field.tooltip
              ? {
                  title: field.tooltip,
                  icon: <InfoCircleOutlined />,
                  overlayStyle: tooltipCSS
                }
              : ''
          }
          className={field?.colSpan ? '' : 'col-span-center'}
          labelCol={field?.colSpan ? {span: 24} : undefined}
        >
          <MomentDatePicker
            style={{verticalAlign: 'top'}}
            name={field.fieldName}
            format={field.dateFormat ?? 'DD-MM-YYYY'}
            showNow={false}
            disabledDate={(current) => {
              return field.restrictFutureDateSelection
                ? moment() <= current
                : false;
            }}
          />
        </Form.Item>
      );
    case RADIOGROUP:
      return (
        <>
          <Form.Item
            name={field.fieldName}
            label={field.fieldLabel}
            tooltip={
              field.tooltip
                ? {
                    title: field.tooltip,
                    icon: <InfoCircleOutlined />,
                    overlayStyle: {
                      width: 'auto',
                      maxWidth: '500px',
                      height: 'auto',
                      padding: '12px'
                    }
                  }
                : ''
            }
            className={field?.colSpan ? '' : 'col-span-center'}
            labelCol={field?.colSpan ? {span: 24} : undefined}
          >
            <Radio.Group
              name={field.fieldName}
              options={field.options}
              value={field.defaultValue}
              onChange={(e) => {
                handleOnChange(
                  field.index + '_' + field.fieldName[1],
                  e.target.value
                );
              }}
              style={{verticalAlign: 'top'}}
            ></Radio.Group>
          </Form.Item>
          <Space size="small"></Space>
          {getFieldValue([field.mainKey, field.index, field.fieldName[1]]) ===
            'Other' || showTextBox[field.index + '_' + field.fieldName[1]] ? (
            <Form.Item name={[field.index, field.fieldName[1] + '_other']}>
              <Input
                placeholder="Other"
                type={
                  getFieldValue([
                    field.mainKey,
                    field.index,
                    field.fieldName[1]
                  ]) === 'Other'
                    ? 'text'
                    : 'hidden'
                }
              />
            </Form.Item>
          ) : (
            <></>
          )}
        </>
      );
    case RADIOGROUP_IF_NO_THEN_TEXTBOX:
      return (
        <>
          <Form.Item
            name={field.fieldName}
            label={field.fieldLabel}
            tooltip={
              field.tooltip
                ? {
                    title: field.tooltip,
                    icon: <InfoCircleOutlined />,
                    overlayStyle: {
                      width: 'auto',
                      maxWidth: '500px',
                      height: 'auto',
                      padding: '12px'
                    }
                  }
                : ''
            }
            className={field?.colSpan ? '' : 'col-span-center'}
            labelCol={field?.colSpan ? {span: 24} : undefined}
          >
            <Radio.Group
              name={field.fieldName}
              options={field.options}
              value={field.defaultValue}
              onChange={(e) => {
                handleOnChange1(
                  field.index + '_' + field.fieldName[1],
                  e.target.value
                );
              }}
              disabled={field.disabled}
              style={{verticalAlign: 'top'}}
            ></Radio.Group>
          </Form.Item>
          <Space size="small"></Space>
          {(getFieldValue([field.mainKey, field.index, field.fieldName[1]]) ===
            'No' ||
            radioGroupIfNoThenShowTextBox[
              field.index + '_' + field.fieldName[1]
            ]) && (
            <Form.Item name={[field.index, field.fieldName[1] + '_no']}>
              <TextArea
                style={{verticalAlign: 'top'}}
                autoSize={{minRows: 4, maxRows: 10}}
                placeholder={field.placeholder}
                disabled={field.disabled}
              />
            </Form.Item>
          )}
        </>
      );
    case CHECKBOX:
      return (
        <Form.Item
          name={field.fieldName}
          label={field.fieldLabel}
          tooltip={
            field.tooltip
              ? {
                  title: field.tooltip,
                  icon: <InfoCircleOutlined />,
                  overlayStyle: tooltipCSS
                }
              : ''
          }
          className={field?.colSpan ? '' : 'col-span-center'}
          labelCol={field?.colSpan ? {span: 24} : undefined}
        >
          <Checkbox
            style={{verticalAlign: 'top'}}
            name={field.fieldName}
          ></Checkbox>
        </Form.Item>
      );
    case CHECKBOXGROUP:
      return (
        <Form.Item
          name={field.fieldName}
          label={field.fieldLabel}
          tooltip={
            field.tooltip
              ? {
                  title: field.tooltip,
                  icon: <InfoCircleOutlined />,
                  overlayStyle: tooltipCSS
                }
              : ''
          }
          className={field?.colSpan ? '' : 'col-span-center'}
          labelCol={field?.colSpan ? {span: 24} : undefined}
        >
          <Checkbox.Group
            options={field.options}
            defaultValue={[field.defaultValue]}
            style={{verticalAlign: 'top'}}
          ></Checkbox.Group>
        </Form.Item>
      );
    default:
      return (
        <Form.Item
          name={field.fieldName}
          label={field.fieldLabel}
          tooltip={
            field.tooltip
              ? {
                  title: field.tooltip,
                  icon: <InfoCircleOutlined />,
                  overlayStyle: tooltipCSS
                }
              : ''
          }
          className={field?.colSpan ? '' : 'col-span-center'}
          labelCol={field?.colSpan ? {span: 24} : undefined}
        >
          <Input
            style={{verticalAlign: 'top'}}
            name={field.fieldName}
            placeholder={field.placeholder}
          />
        </Form.Item>
      );
  }
};

export default QuestionnaireAddMoreFieldsSwitch;
